import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import {
  TextField,
  InputAdornment,
  Paper,
  Box,
  Typography,
  // Chip,
  // Button,
  // CircularProgress,
  // IconButton,
  // Tooltip,
  // Input,
  // Icon,
  Divider,
  Collapse,
  Skeleton,
  IconButton,
  CircularProgress,
  SxProps,
  Button
} from "@mui/material";
import { PinDrop, Search, Close, Cloud } from "@mui/icons-material";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import useClickOutside from "../../../hooks/useClickOutside";

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  onServerSearch?: (value?: string) => void;
  results?: any[];
  serverResults?: any[];
  loading?: boolean;
  serverSearchLoading?: boolean;
  requireEnter?: boolean;
  placeholder?: string;
  showServerSearch?: boolean;
  // Grouped action props
  actions?: {
    onRefresh?: () => void;
    refreshDisabled?: boolean;
    onClear?: () => void;
    additionalButtons?: React.ReactNode;
  };
  disabledDebounce?: boolean;
  searchOnEnter?: (e?: KeyboardEvent, searchTerm?: string) => void;
  forceCleanup?: boolean;
  sx?: SxProps;
}

function SearchResultSkeleton() {
  return (
    <Box sx={{ display: "flex", gap: 6, alignItems: "center" }}>
      <Skeleton variant='circular' height={20} width={20} />
      <Box sx={{ flex: "1" }}>
        <Skeleton variant='text' height={14} width={"90%"} />
        <Skeleton variant='text' height={10} width={"40%"} />
      </Box>
    </Box>
  );
}
function SearchLoading({ loadingCount = 4 }: { loadingCount?: number }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 12, p: "0.375rem 0.5rem" }}>
      {Array.from({ length: loadingCount }).map((_, idx) => (
        <SearchResultSkeleton key={idx} />
      ))}
    </Box>
  );
}

// function highlightMatchText(value: string, matchStr: string) {
//   if (!value?.trim() || matchStr?.trim()) return value;
//   const splitedValue = value?.toLowerCase()?.split(" ");
//   const splitedMatchStr = matchStr?.toLowerCase()?.split(" ");
//   let finalStr = splitedValue.map((value) => {
//     const matchedStr = splitedMatchStr.findIndex((word) => value.startsWith(word));
//
//     if (matchedStr) {
//       if (value === splitedMatchStr[matchedStr]) {
//         return <span>{value} </span>;
//       }
//       const splitMatchedStr = value?.split(splitedMatchStr[matchedStr]);
//       return (
//         <Box sx={{ display: "inline" }}>
//           <span>{splitedMatchStr[matchedStr]}</span>
//         </Box>
//       );
//     }
//     return value;
//   });
//
//   return finalStr;
// }

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSearch,
  onServerSearch,
  results = [],
  serverResults = [],
  loading = false,
  placeholder = "Search...",
  actions,
  disabledDebounce = false,
  serverSearchLoading = false,
  searchOnEnter,
  forceCleanup,
  sx
}) => {
  const [isShowResultDropdown, setIsShowResultDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);

  const { clickOutsideRef, isClickOutside, toggleOutsideClick } = useClickOutside();

  const debouncedSearch = useDebounceCallback((search: string) => {
    onSearch && onSearch(search?.trim());
    onChange && onChange(search?.trim());
  }, 500);

  const isLoading = loading || serverSearchLoading;

  useEffect(() => {
    if (isClickOutside) {
      setIsShowResultDropdown(false);
    }
  }, [isClickOutside, toggleOutsideClick]);

  // Cleanup internal search state if value is empty
  useEffect(() => {
    if (forceCleanup) {
      setSearchTerm("");
    }
  }, [forceCleanup]);

  // Handle enter key press
  useEffect(() => {
    function handleOnEnterKeyPress(event: KeyboardEvent) {
      if (searchOnEnter && event.key === "Enter") {
        searchOnEnter(event, searchTerm);
      }
    }

    window.addEventListener("keydown", handleOnEnterKeyPress);

    return () => {
      window.removeEventListener("keydown", handleOnEnterKeyPress);
    };
  }, [searchOnEnter, searchTerm]);

  // call debounce search if not disable
  function handleSearch(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = e.target;
    if (!disabledDebounce) {
      debouncedSearch(value?.trim());
    } else {
      onSearch && onSearch(value?.trim());
      onChange && onChange(value?.trim());
    }
    // update local search state
    setSearchTerm(value);
  }

  function handleSearchBarFocus() {
    setIsShowResultDropdown(true);
    toggleOutsideClick();
  }

  function handleTriggerServerSearch() {
    if (!searchTerm || serverSearchLoading) return;

    if (onServerSearch) {
      onServerSearch(searchTerm.trim());
    }
  }

  function handleClear() {
    if (isLoading) return;
    if (actions?.onClear) {
      actions?.onClear();
      setSearchTerm("");
    }
  }

  const combinedResults = useMemo(() => {
    return [...results, ...serverResults];
  }, [results, serverResults]);

  const isShowNoResultMsg = (!combinedResults || combinedResults?.length === 0) && searchTerm && !(loading || serverSearchLoading);

  return (
    <Box ref={clickOutsideRef}>
      <Paper
        elevation={1}
        sx={{
          background: "#fff",
          borderRadius: 6,
          // borderRadius: isShowResultDropdown || isClickOutside ? "1rem 1rem 0.75rem 0.75rem" : 6,
          border: 0,
          transition: "border-radius 300ms linear",
          overflow: "hidden"
        }}
      >
        <Box
          component='label'
          sx={{
            display: "flex",
            maxHeight: "3rem",
            height: "3rem",
            gap: "0.5rem",
            alignItems: "center",
            py: "10px",
            boxSizing: "border-box",
            ...sx
          }}
          role='search'
          aria-roledescription='search bar'
        >
          <TextField
            fullWidth
            variant='outlined'
            placeholder={placeholder}
            onFocus={handleSearchBarFocus}
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start' title='search' sx={{ cursor: "pointer" }}>
                  <IconButton size='small' onClick={handleTriggerServerSearch} title={"Get more"} disabled={isLoading}>
                    <Search sx={{ fontSize: "1.5rem", "&:hover": { color: "primary.main" } }} />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: searchTerm?.trim() && (
                <InputAdornment position='end'>
                  {/* `Get more` button */}
                  {onServerSearch && searchTerm && (
                    <Button
                      startIcon={<Cloud style={{ fontSize: "0.6rem", color: "grey.500" }} />}
                      variant='secondary'
                      onClick={handleTriggerServerSearch}
                      disabled={serverSearchLoading}
                      sx={{
                        fontSize: "0.6rem",
                        padding: "0.125rem 0.5rem",
                        gap: 0.4,
                        fontWeight: 600,
                        "& .MuiButton-startIcon": {
                          marginRight: "0.2rem"
                        }
                      }}
                    >
                      Get more
                    </Button>
                  )}

                  {/* Clear search term button */}
                  <IconButton
                    size='small'
                    title={serverSearchLoading ? "Server searching..." : "Clear search"}
                    disabled={isLoading}
                    onClick={handleClear}
                  >
                    {serverSearchLoading ? <CircularProgress size={20} sx={{ color: "primary.main" }} /> : <Close />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              px: 0,
              "&:focus": {
                outline: "none"
              },
              "& .MuiOutlinedInput-root": {
                border: 0,
                "& fieldset": {
                  border: 0
                }
              }
            }}
          />
        </Box>

        {/* Auto complete and search result */}
        <Collapse
          in={
            false
            // isShowResultDropdown || isClickOutside
          }
        >
          <Divider sx={{ background: "#EAECF0" }} />
          <Box sx={{ minHeight: 120, maxHeight: "calc(100dvh - 10rem)", overflowY: "auto" }}>
            {(loading || serverSearchLoading) && <SearchLoading />}
            {/* Render search results */}
            {combinedResults?.length > 0 && (
              <Box>
                {combinedResults?.map((result) => {
                  return (
                    <Box
                      key={result.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: "0.75rem",
                        gap: "0.5rem",
                        boxSizing: "border-box",
                        transition: "background 400ms",
                        cursor: "pointer",
                        ":hover": {
                          background: "#efefef"
                        }
                      }}
                    >
                      <PinDrop sx={{ fontSize: "1rem", color: "#475467" }} />
                      <Typography
                        fontWeight={500}
                        fontSize={"0.875rem"}
                        sx={{
                          color: "#98A2B3",
                          "& span": {
                            color: "#000"
                          }
                        }}
                      >
                        <span style={{ color: "#475467" }}>Warrangala</span> lifestyle
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {isShowNoResultMsg && (
              <Box sx={{ px: 2, py: 60, textAlign: "center" }}>
                <Typography variant='body2'>No results found for "{searchTerm}"</Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default SearchBar;
